import { useParams, useNavigate } from "react-router-dom";
import { useRunPerformance, useCollectMetrics } from "../hooks/usePerformance";
import { useContentRun } from "../hooks/useContent";
import { PerformanceCard } from "../components/performance/PerformanceCard";
import { Button } from "../components/ui/Button";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { LoadErrorState } from "../components/ui/LoadState";

export function Performance() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const {
    data: performanceData,
    isLoading: perfLoading,
    isError: perfFailed,
    refetch: refetchPerformance,
  } = useRunPerformance(runId);
  const { data: runData, isLoading: runLoading } = useContentRun(runId);
  const collectMetrics = useCollectMetrics(runId || "");

  const isLoading = perfLoading || runLoading;

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  const performances = performanceData?.data || [];
  const run = runData?.data;
  const selectedIdea =
    run && run.selected_idea_index !== null
      ? run.ideas[run.selected_idea_index]
      : null;

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title="Performance"
        description={selectedIdea ? selectedIdea.headline : "Inspect collected engagement and performance metrics for this run."}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Performance" },
        ]}
        metadata={(
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
            Metrics / engagement
          </span>
        )}
        actions={(
          <>
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            >
              Back to workspace
            </button>
            <Button
              variant="secondary"
              size="sm"
              loading={collectMetrics.isPending}
              onClick={() => collectMetrics.mutate()}
            >
              Collect Metrics
            </Button>
          </>
        )}
      />

      {perfFailed && !performanceData ? (
        <LoadErrorState
          title="Performance data could not be loaded"
          description="This does not mean there is no performance data."
          onRetry={() => void refetchPerformance()}
        />
      ) : performances.length === 0 ? (
        <div className="flex min-h-[30vh] flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-300 bg-white p-8 shadow-sm">
          <svg
            className="mb-3 h-10 w-10 text-slate-300"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
            />
          </svg>
          <p className="mb-1 text-sm font-medium text-slate-500">
            No performance data yet
          </p>
          <p className="text-xs text-slate-400">
            Click "Collect Metrics" to fetch the latest engagement data.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {performances.map((perf) => (
            <PerformanceCard
              key={perf.id}
              platform={perf.platform}
              metrics={perf.metrics}
              pieceId={perf.piece_id}
              collectedAt={perf.collected_at}
            />
          ))}
        </div>
      )}
    </div>
  );
}
